import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types/auth";
import { authService } from "../services/authService";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: "AUTH_START" }
  | { type: "AUTH_SUCCESS"; payload: { user: User } }
  | { type: "AUTH_FAILURE"; payload: string }
  | { type: "LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_LOADING"; payload: boolean };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "AUTH_FAILURE":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (returnUrl?: string) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string, fullName?: string) => Promise<void>;
  handleCallback: (guid: string, returnUrl?: string) => Promise<string>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing auth on mount
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      const storedUser = authService.getStoredUser();

      if (storedUser) {
        try {
          // Verify session is still valid by calling /auth/me
          const user = await authService.getCurrentUser();
          if (isMounted) {
            dispatch({
              type: "AUTH_SUCCESS",
              payload: { user },
            });
          }
          return;
        } catch (error) {
          // Session invalid - clear storage and stop loading
          if (isMounted) {
            console.warn("Session verification failed, clearing stored user:", error);
            localStorage.removeItem("user");
            dispatch({ type: "SET_LOADING", payload: false });
          }
        }
      } else {
        // No stored user - don't try to verify if we're on auth pages or home page
        // This prevents infinite loops when user is logged out
        const currentPath = window.location.pathname;
        const isAuthPage = currentPath.includes("/auth/callback") || 
                          currentPath.includes("/auth/login");
        const isHomePage = currentPath === "/";
        
        // Only try to verify session if we're on a protected route (not home/auth pages)
        // This prevents unnecessary API calls when user is logged out
        if (!isAuthPage && !isHomePage) {
          try {
            const user = await authService.getCurrentUser();
            if (isMounted) {
              dispatch({
                type: "AUTH_SUCCESS",
                payload: { user },
              });
            }
            return;
          } catch (error) {
            // No valid session - expected if user is logged out
            if (isMounted) {
              dispatch({ type: "SET_LOADING", payload: false });
            }
          }
        } else {
          // On auth/home page without user, just stop loading (don't verify)
          if (isMounted) {
            dispatch({ type: "SET_LOADING", payload: false });
          }
        }
      }
    };

    initializeAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (returnUrl: string = window.location.pathname) => {
    dispatch({ type: "AUTH_START" });
    try {
      // This will redirect to IdP, so we won't return here
      await authService.login(returnUrl);
    } catch (error: any) {
      const errorMessage = error.message || "Login failed";
      dispatch({
        type: "AUTH_FAILURE",
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const loginWithCredentials = async (email: string, password: string) => {
    dispatch({ type: "AUTH_START" });
    try {
      const user = await authService.loginWithCredentials(email, password);
      dispatch({
        type: "AUTH_SUCCESS",
        payload: { user },
      });
    } catch (error: any) {
      const errorMessage = error.message || "Login failed";
      dispatch({
        type: "AUTH_FAILURE",
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const register = async (
    email: string,
    username: string,
    password: string,
    fullName?: string
  ) => {
    dispatch({ type: "AUTH_START" });
    try {
      const user = await authService.register(email, username, password, fullName);
      dispatch({
        type: "AUTH_SUCCESS",
        payload: { user },
      });
    } catch (error: any) {
      const errorMessage = error.message || "Registration failed";
      dispatch({
        type: "AUTH_FAILURE",
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const handleCallback = async (guid: string, returnUrl: string = "/") => {
    dispatch({ type: "AUTH_START" });
    try {
      const redirectTo = await authService.handleCallback(guid, returnUrl);
      
      // Small delay to ensure cookie is processed by browser
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verify the session immediately after callback to ensure cookie is set
      try {
        const user = await authService.getCurrentUser();
        dispatch({
          type: "AUTH_SUCCESS",
          payload: { user },
        });
      } catch (verifyError) {
        // If verification fails, use stored user (cookie might take a moment to propagate)
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          // Set user from storage - cookie will be verified on next request
          dispatch({
            type: "AUTH_SUCCESS",
            payload: { user: storedUser },
          });
        } else {
          throw new Error("Failed to verify session after authentication");
        }
      }
      
      return redirectTo;
    } catch (error: any) {
      const errorMessage = error.message || "Authentication failed";
      dispatch({
        type: "AUTH_FAILURE",
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      await authService.logout();
    } finally {
      dispatch({ type: "LOGOUT" });
    }
  };

  const checkAuth = async () => {
    try {
      const user = await authService.getCurrentUser();
      dispatch({
        type: "AUTH_SUCCESS",
        payload: { user },
      });
    } catch (error) {
      dispatch({ type: "LOGOUT" });
    }
  };

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithCredentials,
    register,
    handleCallback,
    logout,
    clearError,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
